/**
 * «Register» under Medlemmer, and one page per person.  2026-10-15 (membership UX plan, PR A).
 *
 * Before: a read-only register list whose rows went nowhere, and the facts about ONE person spread over three cards
 * with three searches — «Medlemskap per person» (periods and actions), «Husstand og synlighet» (guardians, app
 * accounts) and, on another page, «Papirskjema» (the consent answer). Now the list's rows open the person's page, and
 * that page holds all of it: status, periods, household, app accounts, the consent answer, and the actions.
 *
 * - Every section keeps the permission its endpoint checks (core rule 20): periods need membership.decide, the
 *   household and app accounts members.link, the paper form consent.record_paper. An editor sees what they may use.
 * - App users who are not linked are a second view of the same tab. An account with a WAITING application is not
 *   linked here (owner decision 3, 2026-10-15): the application is decided in «Til behandling», and the server refuses
 *   a link (409 pending_application).
 * - Linking: pick or confirm the member, READ what the link grants, say how you know it is them, then link — two
 *   clicks. The sentence is on the screen before the button (CA-39).
 */
import React, { useEffect, useState } from 'react';
import { can, errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg, useToastOn } from '../lib/hooks';
import { hashQuery } from '../lib/hash';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { AccountIdentity, LinkPreview, MethodField, PersonSearch, StandingTag } from '../components/people';
import { fromCard, fromPeriods } from '../lib/membership-status';
import { PersonPanel, RegisterPerson, personHref } from './Register';
import { PaperConsent } from './Consent';
import { NationalIdCard } from './NationalId';
import { shows as nidShows } from '../lib/national-id';
import { useNav } from '../lib/nav';
// Members register «pro» (2026-10-31): the register list with filters and a selection, and the person page's tabs.
import { MemberRegister } from './MemberRegister';
import { PersonTabs, ProfileFields } from './PersonTabs';
import { genitive, longDate, osloDate } from '../lib/format';

/* ------------------------------------------------------------------------------------------- the tab ------- */
export function RegisterTab() {
  const [view, setView] = useState<'members' | 'unlinked'>(() => (hashQuery().get('vis') === 'ukoblede' ? 'unlinked' : 'members'));
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState('');   // «Registrert som OA-…», a toast once the popup has closed
  useToastOn(done, 'ok');
  const unlinked = useLoad<Body>('/members/unlinked');
  const n = unlinked.data ? unlinked.data.total || 0 : null;
  return (
    <>
      <div className="mreg-bar">
        <div className="bnr-seg" role="group" aria-label="Vis">
          <button type="button" aria-pressed={view === 'members'} onClick={() => setView('members')}>Personer</button>
          <button type="button" aria-pressed={view === 'unlinked'} onClick={() => setView('unlinked')}>Ukoblede appbrukere{n ? ` · ${n}` : ''}</button>
        </div>
        {can('members.register') && <Btn small={false} onClick={() => { setDone(''); setAdding(true); }}>+ Registrer person</Btn>}
      </div>
      {can('members.register') && <RegisterPerson open={adding} onClose={() => setAdding(false)} onCreated={setDone} />}
      {view === 'members'
        ? <MemberRegister importedAt={unlinked.data?.lastImport?.importedAt} />
        : unlinked.error ? <LoadError error={unlinked.error} retry={unlinked.reload} />
        : !unlinked.data ? <Loading /> : <Unlinked data={unlinked.data} onLinked={unlinked.reload} />}
    </>
  );
}

/* --------------------------------------------------------------------------- app users not linked ---------- */
const APPLYING = 'Har meldt seg inn — behandle det under «Til behandling».';

function Unlinked({ data, onLinked }: { data: Body; onLinked: () => void }) {
  const [openRow, setOpenRow] = useState<number | null>(null);
  const items: Body[] = data.items || [];
  const methods: Body[] = data.verificationMethods || [];
  return (
    <Card title={'Ukoblede appbrukere · ' + (data.total || 0)} hint="Innlogging gir ingen medlemskap: personen melder seg inn i appen, eller kontoret kobler."
      more={<p>Her står alle som har logget inn og ikke er koblet til et medlemskap. Et treff i registeret er bare et forslag. Den som har meldt seg inn i appen og venter, behandles under «Til behandling».</p>}>
      {data.allClear_no ? <div className="card-body">{data.allClear_no}</div> : (
        <table className="t">
          <thead><tr><th scope="col">Appbruker</th><th scope="col">Registrert</th><th scope="col">Foreslått medlem</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.map((u, idx) => {
            const st = u.status || {}; const idn = u.identity;
            const suggestion = u.exact ? <>{u.exact.name || 'Treff på nummer'}{u.exact.solidusNo && <> <Tag tone="grey">medl. {u.exact.solidusNo}</Tag></>} <Tag tone="gold">nøyaktig treff</Tag></>
              : (u.candidates || []).length ? <>{u.candidates.length === 1 && idn ? '1 mulig treff. Det er ikke bekreftet.' : st.note_no} <Tag tone="grey">{(u.candidates[0].reasons_no || []).join(', ')}</Tag></>
              : st.note_no || '';
            return (
              <React.Fragment key={u.userId}>
                <tr>
                  <td><AccountIdentity account={u} />{u.stale && <> <Tag tone="red">venter {u.ageDays} d</Tag></>}{idn && idn.reason_no && <div className="sub" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{idn.reason_no}</div>}</td>
                  <td>{osloDate(u.registeredAt)}</td>
                  <td>{suggestion}</td>
                  <td>{u.pendingApplication
                    ? <a href="#/medlemmer">{APPLYING}</a>
                    : <Btn ghost={!u.exact} aria-expanded={openRow === idx} onClick={() => setOpenRow(openRow === idx ? null : idx)}>{u.exact ? (st.action_no || 'Bekreft og koble') : 'Finn medlem'}</Btn>}</td>
                </tr>
                {openRow === idx && !u.pendingApplication && <tr><td colSpan={4}><LinkFlow u={u} methods={methods} onLinked={onLinked} /></td></tr>}
              </React.Fragment>
            );
          })}</tbody>
        </table>
      )}
    </Card>
  );
}

/** Link one app account to one person. `memberId` fixed when it starts from the person's page. */
function LinkFlow({ u, methods, onLinked, fixedMemberId }: { u: Body; methods: Body[]; onLinked: () => void; fixedMemberId?: string }) {
  const [memberId, setMemberId] = useState<string | null>(fixedMemberId || (u.exact ? u.exact.memberId : null));
  const [pv, setPv] = useState<Body | null>(null);
  const [pvErr, setPvErr] = useState('');
  const [method, setMethod] = useState(''); const [note, setNote] = useState('');
  const [msg, say] = useMsg();
  useEffect(() => {
    if (!memberId) return;
    setPv(null); setPvErr('');
    void send(`/members/link-preview?userId=${encodeURIComponent(u.userId)}&memberId=${encodeURIComponent(memberId)}`)
      .then((r) => r.ok ? setPv(r.body) : setPvErr(errText(r.body)));
  }, [memberId, u.userId]);

  if (!memberId) return (
    <div className="confirm"><div>Hvem er dette? Søk på navn eller medlemsnummer{(u.candidates || []).length ? ', eller velg et forslag' : ''}.</div>
      <div className="acts">{(u.candidates || []).map((c: Body) => <Btn key={c.memberId} ghost onClick={() => setMemberId(c.memberId)}>{c.name} · medl. {c.memberNo || c.solidusNo}</Btn>)}</div>
      <PersonSearch label="Søk etter medlem: navn eller medlemsnummer" onPick={(m) => setMemberId(m.id)} />
    </div>
  );
  if (pvErr) return <div className="msg err">{pvErr}</div>;
  if (!pv) return <div className="loading">Henter …</div>;
  return (
    <div className="confirm">
      <LinkPreview pv={pv} />
      <div className="acts">
        <MethodField methods={methods} value={method} onChange={setMethod} />
        <Field label="Notat (påkrevd ved tilbakeringing)" style={{ margin: 0, minWidth: 220 }}>{(id) => <input id={id} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
        <TwoStep ghost={false} label={pv.confirmLabel_no || 'Koble'} confirmLabel="Klikk igjen for å koble" onConfirm={async () => {
          const r = await post('/members/link', { userId: u.userId, memberId, method, note });
          if (r.ok) { say(r.body.message || 'Koblet.', 'ok'); setTimeout(onLinked, 900); } else say(errText(r.body), 'err');
        }} />
      </div>
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/* ------------------------------------------------------------------------------------- a person's page ------- */
export function PersonPage({ id }: { id: string }) {
  const { caps } = useNav();
  const hh = useLoad<Body>(`/members/${encodeURIComponent(id)}/household`);
  const reg = useLoad<Body>(can('membership.decide') ? `/members/${encodeURIComponent(id)}/register` : null);
  const back = <p className="mback"><a href="#/medlemmer/register">← Til registeret</a></p>;
  if (hh.error) return <>{back}<LoadError error={hh.error} retry={hh.reload} /></>;
  if (!hh.data) return <>{back}<Loading /></>;
  const m = hh.data.member;
  const standing = reg.data ? fromPeriods(reg.data.periods || [], Boolean(reg.data.reconciliation)) : fromCard(hh.data.cardState, hh.data.status);
  const membershipActions = can('membership.decide') || can('membership.declaration.record') || can('membership.withdrawal.record') || can('membership.end');
  const overview = <>
      {membershipActions && <Card title="Medlemskap"><CardBody><PersonPanel personId={id} name={m.name} memberNo={m.memberNo || m.solidusNo} data={reg.data} error={reg.error} reload={() => { void reg.reload(); void hh.reload(); }} /></CardBody></Card>}
      <Household data={hh.data} reload={hh.reload} />
      <Accounts data={hh.data} memberId={id} onLinked={hh.reload} />
      {/* Fødselsnummer (2026-10-15): only with the capability national_id AND members.national_id; masked, «Vis» audited. */}
      {nidShows(caps, can) && <NationalIdCard memberId={id} />}
      {can('consent.record_paper') && <Card title="Samtykke" hint="Personens eget svar på om de har meldt seg inn.">
        <CardBody><PaperConsent memberNo={String(m.memberNo || m.solidusNo)} /></CardBody></Card>}
  </>;
  return (
    <>
      {back}
      <Card title={<>{m.name} <span className="mperson-no">medl. {m.memberNo || m.solidusNo}</span></>}>
        <CardBody>
          <div className="mperson-facts"><StandingTag s={standing} />
            {m.age !== null && <span>{m.age} år</span>}
            <span>{hh.data.source === 'native' ? 'Registrert i Masjid' : hh.data.source ? 'Fra importen' : ''}</span></div>
          <ProfileFields key={JSON.stringify(hh.data.profile || null)} id={id} profile={hh.data.profile} onSaved={hh.reload} />
        </CardBody>
      </Card>
      <PersonTabs id={id} overview={overview} />
    </>
  );
}

/* The household (CA-71). Guardianship is recorded by a person until Solidus can supply it; refusals and sentences are core's. */
const SOURCE: Record<string, string> = { staff: 'lagt inn av kontoret', solidus: 'fra importen' };
function Household({ data, reload }: { data: Body; reload: () => void }) {
  const [confirm, setConfirm] = useState<{ pair: Body; text: string } | null>(null);
  const [err, setErr] = useState('');
  useToastOn(err, 'err');
  const [busy, setBusy] = useState(false);
  const m = data.member;
  const childMode = !(m.age !== null && m.age < 15);
  const rows = (list: Body[], kind: 'child' | 'guardian') => list.length ? list.map((x) => (
    <tr key={x.id}><td dir="auto"><a href={personHref(x.id)}>{x.name}</a> · medl. {x.solidusNo}</td><td>{x.age !== null ? x.age + ' år' : 'alder ukjent'}</td>
      <td>{SOURCE[x.source] || x.source}{kind === 'child' ? (x.active ? ` · ser alt til ${x.grantsUntil}` : <> · <Tag tone="grey">over 15 — gir ingen innsyn lenger</Tag></>) : null}</td>
      {/* QA C (2026-10-16): the second click says whose access ends — the adult stops seeing the child's data. */}
      <td><TwoStep label="Fjern" ariaLabel={`Fjern ${x.name} som ${kind === 'child' ? 'barn' : 'foresatt'}`} busyLabel="Fjerner …"
        confirmLabel={kind === 'child' ? `Klikk igjen: ${m.name} ser ikke lenger ${genitive(x.name)} opplysninger` : `Klikk igjen: ${x.name} ser ikke lenger ${genitive(m.name)} opplysninger`} onConfirm={async () => {
        const r = await send('/members/guardian', { method: 'DELETE', body: JSON.stringify({ guardianMemberId: kind === 'child' ? m.id : x.id, childMemberId: kind === 'child' ? x.id : m.id }) });
        if (r.ok) reload(); else setErr(errText(r.body));
      }} /></td></tr>))
    : <tr><td colSpan={4} style={{ color: 'var(--text-muted)' }}>{kind === 'child' ? 'Ingen barn registrert.' : 'Ingen foresatte registrert.'}</td></tr>;
  return (
    <Card title="Husstand og synlighet" hint="Hvem som er foresatt for et barn under 15, og hva andre i husstanden ser."
      more={<p>Standard er lukket: en annen voksen i husstanden ser aldri givehistorikk eller personopplysninger. Når alle logger inn med BankID eller Vipps, bygger ikke husstanden seg selv fra ett telefonnummer lenger — kontoret legger inn foresatte til medlemsregisteret kan levere det selv.</p>}>
      {/* The guardian rule, said once (it was here twice: a box and core's sentence under it). */}
      <CardBody style={{ paddingBottom: 0 }}><p className="note" style={{ margin: 0 }}>{data.rules_no}</p></CardBody>
      <table className="t"><thead><tr><th scope="col">Foresatte</th><th scope="col"><span className="vh">Alder</span></th><th scope="col"><span className="vh">Kilde</span></th><th scope="col"><span className="vh">Handling</span></th></tr></thead><tbody>{rows(data.guardians, 'guardian')}</tbody></table>
      <table className="t"><thead><tr><th scope="col">Barn</th><th scope="col"><span className="vh">Alder</span></th><th scope="col"><span className="vh">Kilde</span></th><th scope="col"><span className="vh">Handling</span></th></tr></thead><tbody>{rows(data.children, 'child')}</tbody></table>
      <CardBody>
        <PersonSearch label={`Legg til ${childMode ? 'barn under 15' : 'foresatt'} — søk`} onPick={async (other) => {
          const pair = childMode ? { guardianMemberId: m.id, childMemberId: other.id } : { guardianMemberId: other.id, childMemberId: m.id };
          setErr('');
          const pre = await post('/members/guardian', pair);
          if (!pre.ok) { setConfirm(null); return setErr(errText(pre.body)); }
          setConfirm({ pair, text: pre.body.confirm_no });
        }} />
        {/* Core's sentence names who will see what, and until when; the button says it once more (QA C, 2026-10-16). */}
        {confirm && <div className="confirm" role="alert"><div>{confirm.text}</div>
          <div className="acts"><Btn disabled={busy} aria-busy={busy || undefined} onClick={async () => {
            setBusy(true);
            const r = await post('/members/guardian', { ...confirm.pair, confirm: true }).finally(() => setBusy(false));
            if (r.ok) { setConfirm(null); reload(); } else setErr(errText(r.body));
          }}>{busy ? 'Legger til …' : 'Legg til og gi innsyn'}</Btn><Btn ghost disabled={busy} onClick={() => setConfirm(null)}>Avbryt</Btn></div></div>}
      </CardBody>
    </Card>
  );
}

const METHOD: Record<string, string> = { bankid_pid: 'BankID', vipps_phone_birth_year: 'Vipps', vipps_name_birth_date: 'Vipps (navn + fødselsdato)', member_no: 'SMS + medlemsnummer', birth_year: 'SMS + fødselsår', email: 'SMS + e-post', staff: 'koblet av kontoret', dob: 'SMS', in_person: 'koblet av kontoret', self_application: 'meldte seg inn i appen' };

/** The app accounts on this membership, and «Koble appbruker»: one of the accounts waiting to be linked. */
function Accounts({ data, memberId, onLinked }: { data: Body; memberId: string; onLinked: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Card title="Appkontoer" hint="Kontoene som er koblet til dette medlemskapet.">
      <CardBody>
        <p style={{ marginTop: 0 }}>{data.accounts.length ? data.accounts.map((a: Body) => METHOD[a.method] || a.method).join(', ') : 'Ingen ennå.'}</p>
        {!open ? <Btn ghost aria-expanded={false} onClick={() => setOpen(true)}>Koble appbruker</Btn>
          : <PickAccount memberId={memberId} onLinked={() => { setOpen(false); onLinked(); }} onClose={() => setOpen(false)} />}
      </CardBody>
    </Card>
  );
}

function PickAccount({ memberId, onLinked, onClose }: { memberId: string; onLinked: () => void; onClose: () => void }) {
  const { data, error, reload } = useLoad<Body>('/members/unlinked');
  const [userId, setUserId] = useState('');
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  const u = items.find((x) => x.userId === userId);
  const label = (x: Body) => (x.identity && x.identity.name ? `${x.identity.name}${x.identity.birthYear ? ' · f. ' + x.identity.birthYear : ''}` : x.phone || x.email || '—')
    + ` (registrert ${osloDate(x.registeredAt)})`;
  return (
    <div>
      {!items.length ? <div>{data.allClear_no || 'Ingen appbrukere venter på å bli koblet.'}</div> : <>
        <div className="acts">
          <Field label="Appbruker som ikke er koblet" style={{ margin: 0, minWidth: 280 }}>{(id) =>
            <select id={id} value={userId} onChange={(e) => setUserId(e.target.value)}><option value="">Velg …</option>
              {items.map((x) => <option key={x.userId} value={x.userId}>{label(x)}{x.pendingApplication ? ' — har en søknad' : ''}</option>)}</select>}</Field>
          <Btn ghost onClick={onClose}>Lukk</Btn>
        </div>
        {u && (u.pendingApplication ? <p className="note"><a href="#/medlemmer">{APPLYING}</a></p>
          : <LinkFlow key={u.userId} u={u} methods={data.verificationMethods || []} onLinked={onLinked} fixedMemberId={memberId} />)}
      </>}
    </div>
  );
}
